/**
 * onboard_send_controller.js — the send card after a business's first quote
 * (Stimulus). Markup: onboard_send in templates/macros/onboarding.html, which
 * says when it is drawn and what it says. Rules: docs/ONBOARDING_DESIGN_RULES.md
 * A1, A5, F4, H3, M1, M4, M6.
 *
 * Two surfaces, told apart by the surface value:
 *
 * - after_save: a non-modal dialog streamed in with the save's answer, in
 *   place of the success toast. It waits, hidden, while the create panel is
 *   still closing (its formHidden, or 1s at most: a hidden dialog[open] would
 *   hold page-check swaps), then rises. While it is up, <body> carries
 *   data-onboard-prompt, which steps the Chip launcher aside (ai_chat.css,
 *   bridge/chip_launcher_controller.js) and the dashboard checklist's action
 *   down to an outline (onboarding.css). Close, or Escape inside it, plays it
 *   out and removes it, then asks page-check for the page under it, which the
 *   save changed. Any navigation takes it away with the body, and the body
 *   mark leaves before Turbo snapshots the page, so Back never restores it.
 * - quote_page: in flow on the quote's page. Close is for good (the server
 *   stores send_prompt_closed and stops drawing it); the card folds away, as
 *   it does when the email modal hands this quote to the sender.
 *
 * Unconfirmed owner email: resend() sends the request every confirm-link
 * button shares (_resend_link.js). The server's words replace the why in
 * place, never as a toast (A5). Sent, the resend hands its cell to the send
 * action; refused, a quiet Ask Chip appears (H3) in place of the way to the
 * client view (preview target), so the card still offers three choices at
 * most (A2), and the way back returns with the send action.
 *
 * Events go through reportProgress (_onboarding_beacon.js), never
 * reportOnboarding, which stops once a business has a quote: exactly when
 * this card exists. send_prompt_shown when it shows (the quote page's once
 * per page view), send_prompt_tapped on the send action, send_prompt_closed
 * on close.
 */
import { Controller } from "@hotwired/stimulus";
import { animationsFinished, swapText } from "./_onboard.js";
import { reportProgress } from "./_onboarding_beacon.js";
import { resendConfirmLink } from "./_resend_link.js";

/** The create panel (forms_controller.js), closing as the card arrives. */
const FORMS = '[data-controller~="forms"]';
/** The sheet takes 280ms to close and the inline panel 300ms. */
const FORM_CLOSE_WAIT_MS = 1000;
const PROMPT_ATTR = "data-onboard-prompt";
const UP = '.onboard-send[data-surface="after_save"][data-state="in"]';
/** Quote page views already reported, so a section repaint or a page-check
 *  swap does not repeat one; leaving the page clears its view. */
const shownViews = new Set();

export default class extends Controller {
    static targets = ["tick", "why", "ask", "resend", "send", "preview"];
    static values = { surface: String, quote: Number, busy: String };

    connect() {
        if (this.floating) {
            // The snapshot Turbo (or skeleton-nav) takes for Back is cloned in
            // the same task as this event, before disconnect() runs.
            this._onBeforeCache = () => this._releaseBody();
            document.addEventListener("turbo:before-cache", this._onBeforeCache);
            this._arrive();
            return;
        }
        this._view = `${this.quoteValue}:${location.pathname}`;
        this._onBeforeVisit = () => this._leavingView();
        document.addEventListener("turbo:before-visit", this._onBeforeVisit);
        this._onHandedOff = (event) => this._handedOff(event);
        window.addEventListener("pdf-email-preview:handed-off", this._onHandedOff);
        this._reportShownOncePerView();
    }

    disconnect() {
        cancelAnimationFrame(this._frame);
        clearTimeout(this._timer);
        if (this._onFormHidden) document.removeEventListener("formHidden", this._onFormHidden);
        if (this._onBeforeCache) document.removeEventListener("turbo:before-cache", this._onBeforeCache);
        if (this._onBeforeVisit) document.removeEventListener("turbo:before-visit", this._onBeforeVisit);
        if (this._onHandedOff) window.removeEventListener("pdf-email-preview:handed-off", this._onHandedOff);
        if (this.floating) this._releaseBody();
    }

    get floating() {
        return this.surfaceValue === "after_save";
    }

    // MARK: after a save

    _arrive() {
        const panel = document.querySelector(FORMS);
        if (panel && panel.style.display !== "none") {
            this._onFormHidden = () => this.show();
            document.addEventListener("formHidden", this._onFormHidden, { once: true });
            this._timer = setTimeout(() => this.show(), FORM_CLOSE_WAIT_MS);
        } else {
            this._frame = requestAnimationFrame(() => this.show());
        }
    }

    async show() {
        if (this.element.dataset.state !== "waiting" || !this.element.isConnected) return;
        clearTimeout(this._timer);
        document.removeEventListener("formHidden", this._onFormHidden);
        this.element.dataset.state = "in";
        document.body.setAttribute(PROMPT_ATTR, "");
        reportProgress("send_prompt_shown", { surface: this.surfaceValue });
        this._takeFocusIfFree();
        await animationsFinished(this.element);
        if (this.hasTickTarget) this.tickTarget.setAttribute("data-done", "");
    }

    /** Into the card only when nothing else holds focus: never mid-task. */
    _takeFocusIfFree() {
        const active = document.activeElement;
        if (active && active !== document.body && !active.closest(FORMS)) return;
        this._returnFocusTo = active;
        this.element.focus({ preventScroll: true });
    }

    // MARK: actions

    tapped(event) {
        if (event.target.closest('[data-onboard-send-target~="send"]')) {
            reportProgress("send_prompt_tapped", { surface: this.surfaceValue });
        }
    }

    async close() {
        if (this.floating && this.element.dataset.state !== "in") return;
        reportProgress("send_prompt_closed", { surface: this.surfaceValue });
        if (!this.floating) {
            await this.fold();
            return;
        }
        const hadFocus = this.element.contains(document.activeElement);
        this.element.dataset.state = "out";
        await animationsFinished(this.element);
        if (hadFocus && this._returnFocusTo?.isConnected) this._returnFocusTo.focus({ preventScroll: true });
        this.element.remove();
        window.chippiePageCheck?.refresh();
    }

    /** Folds the quote page's card away; its empty wrapper stays as the repaint target. */
    async fold() {
        const wrapper = this.element.closest(".onboard-send-fold");
        if (!wrapper || wrapper.hasAttribute("data-folding")) return;
        if (this.element.contains(document.activeElement)) wrapper.focus({ preventScroll: true });
        wrapper.setAttribute("data-folding", "");
        await Promise.all([animationsFinished(this.element), animationsFinished(wrapper)]);
        this.element.remove();
        wrapper.removeAttribute("data-folding");
    }

    async resend() {
        const button = this.resendTarget;
        if (button.disabled) return;
        const label = button.textContent;
        button.disabled = true;
        button.setAttribute("aria-busy", "true");
        const spinner = document.createElement("span");
        spinner.className = "spinner-border spinner-border-sm";
        spinner.setAttribute("aria-hidden", "true");
        button.replaceChildren(spinner, document.createTextNode(this.busyValue));

        const { ok, message } = await resendConfirmLink();
        if (!this.element.isConnected) return;
        const answered = swapText(this.whyTarget, message);
        if (ok) {
            await Promise.all([answered, this._handOverToSend()]);
            return;
        }
        button.replaceChildren(label);
        button.disabled = false;
        button.removeAttribute("aria-busy");
        this.askTarget.hidden = false;
        if (this.hasPreviewTarget) this.previewTarget.hidden = true;
        button.focus({ preventScroll: true });
    }

    askChip() {
        document.dispatchEvent(new CustomEvent("chippie:open-chip"));
    }

    // MARK: helpers

    /** The link is on its way, so sending is what is left: the resend gives up its cell. */
    async _handOverToSend() {
        const resend = this.resendTarget;
        const send = this.sendTarget;
        this.askTarget.hidden = true;
        if (this.hasPreviewTarget) this.previewTarget.hidden = false;
        resend.dataset.motion = "leave";
        send.hidden = false;
        send.dataset.motion = "enter";
        send.focus({ preventScroll: true });
        await Promise.all([animationsFinished(resend), animationsFinished(send)]);
        resend.remove();
        delete send.dataset.motion;
    }

    _handedOff(event) {
        const detail = event.detail || {};
        if (detail.entityType === "quote" && String(detail.id) === String(this.quoteValue)) this.fold();
    }

    /** The body mark goes with the last card up, so nothing stays stepped aside. */
    _releaseBody() {
        const anotherUp = [...document.querySelectorAll(UP)].some((card) => card !== this.element);
        if (!anotherUp) document.body.removeAttribute(PROMPT_ATTR);
    }

    /** Leaving the page ends its view; page-check's swap repaints the same view. */
    _leavingView() {
        if (!window.chippiePageCheck?.isSwapping()) shownViews.delete(this._view);
    }

    _reportShownOncePerView() {
        if (shownViews.has(this._view)) return;
        shownViews.add(this._view);
        reportProgress("send_prompt_shown", { surface: this.surfaceValue });
    }
}
